import { Stack, Text, Center, Group, TextInput, Button } from '@mantine/core';
import { Database, Search, X, Download } from 'lucide-react';
import { useState, useMemo } from 'react';
import { CoachSurveyTabTable } from './CoachSurveyTabTable';
import { useDataLibraryCoachSurveyResponses } from './useDataLibraryCoachSurveyResponses';
import { FilterPanel } from '../../common/FilterPanel/FilterPanel';
import type { FilterFieldConfig } from '../../common/FilterPanel/FilterPanel';
import { SortPanel } from '../../common/SortPanel/SortPanel';
import type { SortFieldConfig } from '../../common/SortPanel/SortPanel';
import { GroupPanel } from '../../common/GroupPanel/GroupPanel';
import { SavedViewsMenu } from '../components/SavedViewsMenu';
import type { GroupState } from '../../common/GroupPanel/GroupPanel';
import { deriveGroupFields } from '../../../utils/groupDataByField';
import { ColumnSettingsPopover } from '../../common/ColumnSettingsPopover/ColumnSettingsPopover';
import { ExportModal } from '../../common/ExportModal/ExportModal';
import { exportSummaryProps, splitScreenFilters } from '../../common/ExportModal/filterSummary';
import type { SortState } from '../../../utils/sortTasks';
import { clearThenFocusFilter } from '../../../utils/focusFilterTrigger';

interface DataLibraryCoachSurveyTabProps {
  selectedCohortIds: string[];
  surveyType: 'midpoint' | 'endpoint';
  emptyMessage: string;
  noConfigMessage: string;
  activeGroup: GroupState | null;
  onGroupChange: (group: GroupState | null) => void;
}

const SORT_FIELDS: SortFieldConfig[] = [
  { value: 'first_name', label: 'Coach Name' },
  { value: 'email', label: 'Email' },
  { value: 'submitted_at', label: 'Submitted Date' },
];

export function DataLibraryCoachSurveyTab({
  selectedCohortIds,
  surveyType,
  emptyMessage,
  noConfigMessage,
  activeGroup,
  onGroupChange,
}: DataLibraryCoachSurveyTabProps) {
  const {
    rows,
    groupedRows,
    hasSurveyConfig,
    questions,
    loading,
    totalCount,
    totalPages,
    filterOptions,
    page,
    setPage,
    pageSize,
    setPageSize,
    searchValue,
    setSearchValue,
    sortConfig,
    handleSort,
    handleSortPanelChange,
    activeFilters,
    handleFilterAdd,
    handleFilterChange,
    handleFilterRemove,
    clearAllFilters,
    hasActiveFilters,
    columns,
    setColumns,
    visibleColumns,
    groupLimitTotal,
  } = useDataLibraryCoachSurveyResponses({ selectedCohortIds, surveyType, activeGroup, onGroupChange });

  const [exportOpen, setExportOpen] = useState(false);
  const groupFields = useMemo(() => deriveGroupFields(columns), [columns]);

  const filterFields: FilterFieldConfig[] = useMemo(() => {
    const fields: FilterFieldConfig[] = [
      {
        key: 'overall_satisfaction',
        label: 'Overall Satisfaction',
        type: 'select' as const,
        options: [
          { value: 'Very satisfied', label: 'Very satisfied' },
          { value: 'Satisfied', label: 'Satisfied' },
          { value: 'Neutral', label: 'Neutral' },
          { value: 'Dissatisfied', label: 'Dissatisfied' },
          { value: 'Very dissatisfied', label: 'Very dissatisfied' },
        ],
      },
    ];
    if (filterOptions.cohorts.length > 1) {
      fields.unshift({
        key: 'cohort',
        label: 'Cohort',
        type: 'select' as const,
        options: filterOptions.cohorts,
      });
    }
    return fields;
  }, [filterOptions]);

  const activeSortState: SortState | null = sortConfig
    ? { field: sortConfig.key, direction: sortConfig.order === 'ASC' ? 'asc' : 'desc' }
    : null;

  if (selectedCohortIds.length === 0) {
    return (
      <Center h={300}>
        <Stack align="center" gap="sm">
          <Database size={40} color="#888" />
          <Text c="dimmed" size="lg">
            Select one or more cohorts to view coach feedback
          </Text>
          <Text c="dimmed" size="sm">
            Use the side panel to choose cohorts
          </Text>
        </Stack>
      </Center>
    );
  }

  const entityLabel = surveyType === 'midpoint' ? 'midpoint responses' : 'endpoint responses';
  const hidePagination = !!groupedRows;

  return (
    <Stack gap="sm">
      {/* Toolbar */}
      <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
        <Group gap="xs" wrap="wrap">
          <TextInput
            placeholder={`Search ${entityLabel}...`}
            size="sm"
            leftSection={<Search size={14} />}
            rightSection={
              searchValue ? (
                <X
                  size={14}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSearchValue('')}
                />
              ) : null
            }
            value={searchValue}
            onChange={e => setSearchValue(e.currentTarget.value)}
            w={220}
          />

          <FilterPanel
            quickFilters={[]}
            activeQuickFilters={[]}
            onQuickFilterToggle={() => {}}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            onClearAll={clearAllFilters}
            size="sm"
          />

          <SortPanel
            sortFields={SORT_FIELDS}
            activeSort={activeSortState}
            defaultSort={{ field: 'first_name', direction: 'asc' }}
            onSortChange={handleSortPanelChange as (sort: SortState | null) => void}
            size="sm"
          />

          <GroupPanel
            limitTotal={groupLimitTotal}
            // Filters here run in the browser, so only search changes the server total.
            narrowBy="search"
            groupFields={groupFields}
            activeGroup={activeGroup}
            onGroupChange={onGroupChange}
            size="sm"
          />

          <SavedViewsMenu
            columns={columns}
            onColumnsChange={setColumns}
            filterFields={filterFields}
            activeFilters={activeFilters}
            onFilterAdd={handleFilterAdd}
            onFilterChange={handleFilterChange}
            onFilterRemove={handleFilterRemove}
            sortFields={SORT_FIELDS}
            activeSort={activeSortState}
            onSortChange={handleSortPanelChange as (sort: SortState | null) => void}
            groupFields={groupFields}
            activeGroup={activeGroup}
            onGroupChange={onGroupChange}
            size="sm"
          />

          {hasActiveFilters && (
            <Button variant="subtle" size="sm" color="gray" leftSection={<X size={14} />} onClick={clearThenFocusFilter(clearAllFilters)}>
              Clear
            </Button>
          )}
        </Group>

        <Group gap="xs">
          <Text size="sm" c="dimmed">
            {totalCount} response{totalCount !== 1 ? 's' : ''}
          </Text>
          <ColumnSettingsPopover columns={columns} onColumnsChange={setColumns} zIndex={1000} />
          <Button
            variant="outline"
            color="gray"
            bg="white"
            size="sm"
            style={{ borderColor: '#E8E6E3' }}
            leftSection={<Download size={14} />}
            onClick={() => setExportOpen(true)}
          >
            Export
          </Button>
        </Group>
      </Group>

      <CoachSurveyTabTable
        data={rows}
        groupedData={groupedRows}
        columns={visibleColumns}
        questions={questions}
        sortConfig={sortConfig}
        onSort={handleSort}
        loading={loading}
        hasActiveFilters={hasActiveFilters && hasSurveyConfig}
        onClearFilters={clearAllFilters}
        emptyMessage={!hasSurveyConfig ? noConfigMessage : emptyMessage}
        page={page}
        pageSize={pageSize}
        totalCount={totalCount}
        totalPages={totalPages}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        hidePagination={hidePagination}
      />

      <ExportModal
        opened={exportOpen}
        onClose={() => setExportOpen(false)}
        entityType="survey_responses"
        contextType="global"
        scopeContext={{ cohortIds: selectedCohortIds, surveyKind: 'coach_survey', surveyTypes: [surveyType] }}
        {...exportSummaryProps(splitScreenFilters({ search: searchValue, fields: filterFields, rows: activeFilters }, []))}
        currentViewColumns={columns.filter(c => c.visible).map(c => c.id)}
        gridColumns={columns}
        recordCount={totalCount}
      />
    </Stack>
  );
}
